import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import { GridToolbar } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DownloadIcon from '@mui/icons-material/Download';
import { debounce } from 'lodash';
import '../App.css';

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const AddToBetSlipCell = React.memo(({ row, betSlip, setBetSlip, maxLegs }) => {
  const id = row.id;
  const isAdded = betSlip.includes(id);
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <IconButton
        onClick={() => {
          if (betSlip.length >= maxLegs && !isAdded) return;
          const updated = isAdded
            ? betSlip.filter(x => x !== id)
            : [...betSlip, id];
          setBetSlip(updated);
        }}
        disabled={betSlip.length >= maxLegs && !isAdded}
        size="small"
        sx={{ padding: 0, color: isAdded ? 'green' : 'gray', '&.Mui-disabled': { color: 'gray' } }}
      >
        {isAdded ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      </IconButton>
    </Box>
  );
});

const PickemDataGrid = ({ bets }) => {
  // State
  const [sortModel, setSortModel] = useState([{ field: 'trueWin', sort: 'desc' }]);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [leagueFilter, setLeagueFilter] = useState([]);
  const [betTypeFilter, setBetTypeFilter] = useState([]);
  const [bookFilter, setBookFilter] = useState('');
  const [betSlip, setBetSlip] = useState([]);
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const gridContainerRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);

  // Cache for associated bets
  const associatedBetsCache = useRef(new Map());

  // Clear bet slip when book filter changes
  useEffect(() => {
    setBetSlip([]);
  }, [bookFilter]);

  // Calculate grid width on mount and resize
  useEffect(() => {
    const updateGridWidth = debounce(() => {
      if (gridContainerRef.current) {
        setGridWidth(gridContainerRef.current.offsetWidth);
      }
    }, 100);
    updateGridWidth();
    window.addEventListener('resize', updateGridWidth);
    return () => {
      window.removeEventListener('resize', updateGridWidth);
      updateGridWidth.cancel();
    };
  }, []);

  // Process bets and filter for pick'em bets
  const processedBets = useMemo(() => {
    if (!Array.isArray(bets)) {
      console.log('Pickem bets prop is not an array:', bets);
      return [];
    }

    const pickemBets = bets.filter(bet => bet.pickem === 1 || bet.pickem === true);

    const processed = pickemBets.map((row, idx) => {
      const id = row.id ?? `${idx}-${Date.now()}`;
      const displayName = row.playerName?.trim() || row.teamName || '';
      let side = row.side || '';
      if (side === 'Over') side = 'o';
      if (side === 'Under') side = 'u';
      return {
        ...row,
        id,
        playerName: displayName,
        sidepoints: `${side}${row.points || ''}`.trim(),
      };
    });

    console.log('Processed bets for Pickem:', processed.length, 'items');
    console.log('Sample processed bets:', processed.slice(0, 3).map(bet => ({
      id: bet.id,
      leagueName: bet.leagueName,
      betTypeName: bet.betTypeName,
      marketSourceName: bet.marketSourceName,
      eventShortName: bet.eventShortName,
      playerName: bet.playerName,
      side: bet.side,
      points: bet.points,
      trueWin: bet.trueWin,
      betMarketId: bet.betMarketId,
      pickem: bet.pickem,
    })));

    return processed;
  }, [bets]);

  // Compute filter options directly
  const filterOptions = useMemo(() => {
    const leagues = new Set();
    const betTypes = new Set();
    const books = new Set();

    processedBets.forEach(bet => {
      if (bet.leagueName) leagues.add(bet.leagueName);
      if (bet.betTypeName) betTypes.add(bet.betTypeName);
      if (bet.marketSourceName) books.add(bet.marketSourceName);
    });

    console.log('Available books in Pickem data:', [...books]);

    return {
      leagues: [...leagues],
      betTypes: [...betTypes],
      books: [...books],
    };
  }, [processedBets]);

  // Set default bookFilter based on available books
  useEffect(() => {
    if (filterOptions.books.length > 0 && !bookFilter) {
      const defaultBook = [...filterOptions.books].find(book =>
        book.includes('Underdog Fantasy') ||
        book.includes('PrizePicks') ||
        book.includes('DraftKings Pick6')
      ) || filterOptions.books[0];
      setBookFilter(defaultBook);
    }
  }, [filterOptions.books, bookFilter]);

  useEffect(() => {
    if (filterOptions.leagues.length > 0 && leagueFilter.length === 0) {
      setLeagueFilter(filterOptions.leagues);
    }
    if (filterOptions.betTypes.length > 0 && betTypeFilter.length === 0) {
      setBetTypeFilter(filterOptions.betTypes);
    }
  }, [filterOptions]);

  // Filter setters
  const debouncedSetLeagueFilter = setLeagueFilter;
  const debouncedSetBetTypeFilter = setBetTypeFilter;
  const debouncedSetBookFilter = setBookFilter;

  // Compute filtered bets
  const filteredBets = useMemo(() => {
    if (!processedBets.length) {
      console.log('No processed bets to filter');
      return [];
    }

    let filtered = processedBets;

    if (leagueFilter.length > 0) {
      const leagueSet = new Set(leagueFilter);
      filtered = filtered.filter(bet => !bet.leagueName || leagueSet.has(bet.leagueName));
    }
    if (betTypeFilter.length > 0) {
      const betTypeSet = new Set(betTypeFilter);
      filtered = filtered.filter(bet => !bet.betTypeName || betTypeSet.has(bet.betTypeName));
    }
    if (bookFilter) {
      filtered = filtered.filter(bet => !bet.marketSourceName || bet.marketSourceName === bookFilter);
    }

    console.log('Filtered bets for Pickem table:', filtered.length, 'items');
    console.log('Sample filtered bets:', filtered.slice(0, 3).map(bet => ({
      id: bet.id,
      leagueName: bet.leagueName,
      betTypeName: bet.betTypeName,
      marketSourceName: bet.marketSourceName,
    })));
    return filtered;
  }, [
    processedBets,
    leagueFilter,
    betTypeFilter,
    bookFilter,
  ]);

  // Compute bets in the bet slip
  const betSlipBets = useMemo(() => {
    return processedBets.filter(bet => betSlip.includes(bet.id));
  }, [processedBets, betSlip]);

  // Calculate Odds, Payout, and EV based on the selected book
  const slipWinInfo = useMemo(() => {
    if (betSlipBets.length === 0) return { odds: '-', payout: '-', ev: 0, maxLegs: 8 };

    const slipWinDecimal = betSlipBets.length > 0
      ? betSlipBets.reduce((acc, bet) => {
          const modelWin = bet.trueWin ?? 0;
          return acc * modelWin;
        }, 1)
      : 0;

    let odds = 'N/A';
    if (slipWinDecimal > 0 && slipWinDecimal < 1) {
      odds = Math.round(100 / slipWinDecimal - 100);
      odds = odds > 0 ? `+${odds}` : odds.toString();
    }

    let payout = '-';
    let ev = 0;
    let maxLegs = 8;
    const numLegs = betSlipBets.length;

    if (bookFilter && bookFilter.includes('Underdog Fantasy') && numLegs >= 2) {
      const payouts = {
        2: 200,
        3: 500,
        4: 900,
        5: 1900,
        6: 3400,
        7: 6400,
        8: 11900,
      };
      const payoutOdds = payouts[numLegs];
      maxLegs = 8;
      if (payoutOdds) {
        payout = `+${payoutOdds}`;
        const impliedProb = 100 / (payoutOdds + 100);
        const slipWinDecimalPercent = slipWinDecimal;
        ev = (slipWinDecimalPercent * (payoutOdds / 100)) - (1 - slipWinDecimalPercent);
        ev = ev * 100;
      }
    } else if (bookFilter && bookFilter.includes('PrizePicks') && numLegs >= 2) {
      const payouts = {
        2: 200,
        3: 400,
        4: 900,
        5: 1900,
        6: 3650,
      };
      const payoutOdds = payouts[numLegs];
      maxLegs = 6;
      if (payoutOdds) {
        payout = `+${payoutOdds}`;
        const impliedProb = 100 / (payoutOdds + 100);
        const slipWinDecimalPercent = slipWinDecimal;
        ev = (slipWinDecimalPercent * (payoutOdds / 100)) - (1 - slipWinDecimalPercent);
        ev = ev * 100;
      }
    } else if (bookFilter && bookFilter.includes('DraftKings Pick6') && numLegs >= 2) {
      maxLegs = 8;
    }

    return { odds, payout, ev, maxLegs };
  }, [betSlipBets, bookFilter]);

  useEffect(() => {
    setCurrentPage(0);
  }, [
    leagueFilter,
    betTypeFilter,
    bookFilter,
    processedBets,
  ]);

  const handleFilterChange = (e, options, setter, debouncedSetter) => {
    const val = e.target.value;
    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = prev.length === options.length ? [] : options;
        debouncedSetter(newValue);
        return newValue;
      });
    } else {
      debouncedSetter(val);
    }
  };

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple (${sel.length})` : sel;
  };

  const renderBookFilterValue = (sel, opts, label) => {
    return sel || label;
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (!filteredBets.length) return;

    const headers = [
      'League',
      'Event',
      'Event Start',
      'Participant',
      'Bet Type',
      'Line',
      'Model%',
    ];

    const rows = filteredBets.map(bet => {
      const eventStart = bet.eventStart
        ? new Date(bet.eventStart).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }).replace(',', '')
        : '';
      const model = bet.trueWin == null ? '' : `${(bet.trueWin * 100).toFixed(1)}%`;

      return [
        bet.leagueName || '',
        bet.eventShortName || '',
        eventStart,
        bet.playerName || '',
        bet.betTypeName || '',
        bet.sidepoints || '',
        model,
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `pickem_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Static column definitions
  const staticColumns = [
    {
      field: 'eventShortName',
      headerName: 'Event',
      flex: 2,
      minWidth: 180,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Event
        </Typography>
      ),
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        const [awayTeam, homeTeam] = params.value.split('@');
        const leagueName = params.row.leagueName || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {/* League name */}
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0 }}>
                    {leagueName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mx: 0.5 }}>
                   |
                  </Typography>
                </>
              )}
              {/* Away team */}
              {params.row.awayLogo && (
                <img
                  src={params.row.awayLogo}
                  alt={`${awayTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{awayTeam}</Typography>
              <Typography sx={{ fontSize: '12px', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {/* Home team */}
              {params.row.homeLogo && (
                <img
                  src={params.row.homeLogo}
                  alt={`${homeTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      flex: 2,
      minWidth: 200,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Bet Info
        </Typography>
      ),
      renderCell: (params) => {
        const playerName = params.row.playerName || '';
        const betTypeName = params.row.betTypeName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={playerName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  paddingLeft: '8px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {playerName}
              </Typography>
            </Tooltip>
            <Tooltip title={betTypeName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  paddingLeft: '8px',
                  color: '#BBBBBB',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {betTypeName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    { 
      field: 'sidepoints', 
      headerName: 'Line', 
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Line
        </Typography>
      ),
      renderCell: params => {
        const sidepoints = params.value || '';
        let prefix = sidepoints[0] || ''; // 'u' or 'o'
        let numberStr = sidepoints.slice(1); // '250.5' or '20' or '2.5'
        let formattedNumber = '';
        let paddedPrefix = '      '; // Default spaces for alignment
    
        if (sidepoints && numberStr) {
          const numberParts = numberStr.split('.');
          const integerPart = numberParts[0] || ''; // '250' or '20' or '2'
          const decimalPart = numberParts[1] || '0'; // '5' or undefined (becomes '0')
          
          if (integerPart) { // Valid number part
            formattedNumber = `${integerPart}.${decimalPart}`; // Always includes decimal
            
            // Calculate padding based on the FORMATTED number length (which always includes .X)
            const formattedLength = formattedNumber.length;
            const maxLength = 6; // Accommodate numbers like "250.5" (5 chars + 1 for safety)
            const spaces = maxLength - formattedLength;
            paddedPrefix = spaces > 0 ? ' '.repeat(spaces) + prefix : prefix;
          }
        }
    
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', whiteSpace: 'pre', fontFamily: 'monospace' }}>
              {sidepoints ? `${paddedPrefix}${formattedNumber}` : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'trueWin',
      headerName: 'Model%',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Model%
        </Typography>
      ),
      valueFormatter: v => (v == null ? '' : `${(v * 100).toFixed(1)}%`)
    },
  ];

  // Dynamic columns
  const columns = useMemo(() => [
    ...staticColumns,
    {
      field: 'addToBetSlip',
      headerName: 'Add to Bet Slip',
      sortable: false,
      width: 90,
      flex: 1,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Add to Bet Slip
        </Typography>
      ),
      renderCell: p => (
        <AddToBetSlipCell
          row={p.row}
          betSlip={betSlip}
          setBetSlip={setBetSlip}
          maxLegs={slipWinInfo.maxLegs}
        />
      )
    }
  ], [betSlip, slipWinInfo.maxLegs]);

  // Precompute associated bets with non-pick'em bets only
  const associatedBetsMap = useMemo(() => {
    const map = new Map();
    bets.forEach((bet, idx) => {
      if (!bet.betMarketId || bet.pickem) return; // Exclude pick'em bets
      const group = map.get(bet.betMarketId) || [];
      group.push({ bet, idx });
      map.set(bet.betMarketId, group);
    });
    const result = new Map();
    map.forEach((group, betMarketId) => {
      const betsWithSource = group.map(({ bet, idx }) => ({ ...bet, originalIndex: idx }));
      const sourceMap = new Map();
      betsWithSource.forEach(bet => {
        const source = bet.marketSourceName;
        const list = sourceMap.get(source) || [];
        list.push(bet);
        sourceMap.set(source, list);
      });
      const sortedSources = [...sourceMap.keys()].sort();
      result.set(betMarketId, { sourceMap, sortedSources });
    });
    return result;
  }, [bets]);

  // Lazy computation for associated bets
  const getAssociatedBets = useCallback((row) => {
    if (!row.betMarketId) return [];
    const cacheKey = `${row.betMarketId}-${row.marketSourceName}`;
    if (associatedBetsCache.current.has(cacheKey)) {
      return associatedBetsCache.current.get(cacheKey);
    }
    const data = associatedBetsMap.get(row.betMarketId);
    if (!data) return [];
    const { sourceMap, sortedSources } = data;
    const associated = [];

    sortedSources.forEach(source => {
      const betsForSource = sourceMap.get(source);
      associated.push(...betsForSource);
    });

    // Sort by odds (best to worst)
        // Sort totals by line, then fall back to odds
        const sortedBets = associated.sort((a, b) => {
          // for Unders: highest line first
          if (a.side === 'Under' && b.side === 'Under') {
            return Number(b.points) - Number(a.points);
          }
          // for Overs: lowest line first
          if (a.side === 'Over' && b.side === 'Over') {
            return Number(a.points) - Number(b.points);
          }
      let oddsA = a.americanOdds;
      let oddsB = b.americanOdds;
      if (!oddsA && a.breakEvenWin) {
        const breakEvenWin = Number(a.breakEvenWin);
        if (breakEvenWin > 0 && breakEvenWin < 1) {
          oddsA = breakEvenWin >= 0.5 ? -100 / (1 / breakEvenWin - 1) : (1 / breakEvenWin - 1) * 100;
          oddsA = Math.round(oddsA);
        }
      }
      if (!oddsB && b.breakEvenWin) {
        const breakEvenWin = Number(b.breakEvenWin);
        if (breakEvenWin > 0 && breakEvenWin < 1) {
          oddsB = breakEvenWin >= 0.5 ? -100 / (1 / breakEvenWin - 1) : (1 / breakEvenWin - 1) * 100;
          oddsB = Math.round(oddsB);
        }
      }
      oddsA = oddsA || -Infinity;
      oddsB = oddsB || -Infinity;
      return oddsB - oddsA; // Best to worst
    });

    associatedBetsCache.current.set(cacheKey, sortedBets);
    return sortedBets;
  }, [associatedBetsMap]);

  // Clear cache periodically
  useEffect(() => {
    const interval = setInterval(() => {
      associatedBetsCache.current.clear();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Memoized detail panel item
  const DetailPanelItem = React.memo(({ index, style, data }) => {
    const bet = data.bets[index];
    const side = bet.side === 'Over' ? 'o' : bet.side === 'Under' ? 'u' : '';
    const line = bet.points != null ? bet.points : '';
    let displayOdds = bet.americanOdds;
    if (!displayOdds && bet.breakEvenWin) {
      const breakEvenWin = Number(bet.breakEvenWin);
      if (breakEvenWin > 0 && breakEvenWin < 1) {
        let oddsValue;
        if (breakEvenWin >= 0.5) {
          oddsValue = -100 / (1 / breakEvenWin - 1);
        } else {
          oddsValue = (1 / breakEvenWin - 1) * 100;
        }
        oddsValue = Math.round(oddsValue);
        displayOdds = oddsValue >= 0 ? `+${oddsValue}` : oddsValue.toString();
      }
    }
    displayOdds = displayOdds || 'N/A';

    return (
      <Box
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: '0 11px',
          border: 'none',
          borderRadius: '4px',
          width: '100%',
        }}
      >
        <Box sx={{ mr: 0.5, transform: 'translateY(3px)' }}>
          <img
            src={bet.logourl}
            alt="Book Logo"
            style={{ width: 44, height: 44, objectFit: 'contain' }}
            loading="lazy"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap'}}>
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              mr: 0.5,
              whiteSpace: 'nowrap',
              lineHeight: '44px',
              fontSize: '0.875rem',
            }}
          >
            {(side + line) || '-'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              whiteSpace: 'nowrap',
              lineHeight: '44px',
              fontSize: '0.875rem',
            }}
          >
            {displayOdds}
          </Typography>
        </Box>
      </Box>
    );
  });

  // Detail panel content
  const getDetailPanelContent = useCallback(({ row }) => {
    const associatedBets = getAssociatedBets(row);
    
    if (!associatedBets.length) {
      return (
        <Box sx={{ p: 1, color: '#ffffff' }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>No other books have this bet.</Typography>
        </Box>
      );
    }

    const fixedItemWidth = 150;

    return (
      <Box
        sx={{
          p: '2px 0',
          backgroundColor: '#2a2a2a',
          borderRadius: '4px',
          overflowX: 'auto',
          overflowY: 'hidden',
          display: 'flex',
          flexWrap: 'nowrap',
          height: 52,
        }}
      >
        {associatedBets.map((bet, index) => (
          <Box
            key={index}
            sx={{
              width: fixedItemWidth,
              flexShrink: 0,
              flexGrow: 0,
              display: 'flex',
              alignItems: 'center',
              borderRight: '1px solid #ffffff',
              pr: '11px',
              backgroundColor: '#2a2a2a',
            }}
          >
            <DetailPanelItem
              index={index}
              data={{ bets: associatedBets }}
            />
          </Box>
        ))}
      </Box>
    );
  }, [getAssociatedBets, gridWidth]);

  const getDetailPanelHeight = useCallback(() => 52, []);

  const handleDetailPanelExpanded = useCallback((newExpandedRowIds) => {
    setExpandedRowIds(new Set(newExpandedRowIds));
  }, []);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Filter Section (Dropdowns) */}
          <Box className="filter-section">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                {/* Book Filter (First) */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="book-filter-label" shrink sx={{ color: '#ffffff' }}>Book</InputLabel>
                  <Select
                    labelId="book-filter-label"
                    label="Book"
                    notched
                    value={bookFilter}
                    onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, debouncedSetBookFilter)}
                    renderValue={sel => renderBookFilterValue(sel, filterOptions.books, 'Book')}
                  >
                    {filterOptions.books.map(o => (
                      <MenuItem key={o} value={o}>
                        <ListItemText primary={o} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* League Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="league-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
                  <Select
                    multiple
                    labelId="league-filter-label"
                    label="League"
                    notched
                    value={leagueFilter}
                    onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, debouncedSetLeagueFilter)}
                    renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filterOptions.leagues.map(o => (
                      <MenuItem key={o} value={o}>
                        <Checkbox checked={leagueFilter.includes(o)} />
                        <ListItemText primary={o} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Bet Type Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="bet-type-filter-label" shrink sx={{ color: '#ffffff' }}>Bet Type</InputLabel>
                  <Select
                    multiple
                    labelId="bet-type-filter-label"
                    label="Bet Type"
                    notched
                    value={betTypeFilter}
                    onChange={e => handleFilterChange(e, filterOptions.betTypes, setBetTypeFilter, debouncedSetBetTypeFilter)}
                    renderValue={sel => renderFilterValue(sel, filterOptions.betTypes, 'Bet Type')}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={betTypeFilter.length === filterOptions.betTypes.length} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filterOptions.betTypes.map(o => (
                      <MenuItem key={o} value={o}>
                        <Checkbox checked={betTypeFilter.includes(o)} />
                        <ListItemText primary={o} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Export to CSV">
                  <IconButton
                    onClick={exportToCSV}
                    sx={{
                      color: '#bbbbbb',
                      '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={0} sx={{ height: '100%' }}>
              {/* Table */}
              <Box sx={{ width: '75%' }}>
                <Box className="table-section">
                  <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }} ref={gridContainerRef}>
                    <DataGridPro
                      rows={filteredBets}
                      columns={columns}
                      pageSize={pageSize}
                      onPageSizeChange={setPageSize}
                      rowsPerPageOptions={[50, 100, 250]}
                      pagination
                      paginationMode="client"
                      page={currentPage}
                      onPageChange={setCurrentPage}
                      rowCount={filteredBets.length}
                      sortingMode="client"
                      sortModel={sortModel}
                      onSortModelChange={newModel => setSortModel(newModel)}
                      disableSelectionOnClick
                      disableColumnFilter
                      disableColumnMenu
                      loading={false}
                      getRowId={row => row.id}
                      getRowClassName={(params) => params.row.isDetail ? 'detail-row' : ''}
                      getDetailPanelContent={getDetailPanelContent}
                      getDetailPanelHeight={getDetailPanelHeight}
                      detailPanelExpandedRowIds={expandedRowIds}
                      onDetailPanelExpandedRowIdsChange={handleDetailPanelExpanded}
                      hideFooterSelectedRowCount
                      initialState={{
                        columns: { columnVisibilityModel: { marketSourceName: false } },
                      }}
                      components={{ Toolbar: GridToolbar }}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#1e1e1e',
                          borderBottom: '2px solid #444',
                          position: 'sticky',
                          top: '56px',
                          zIndex: 2,
                        },
                        '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
   
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                          borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
                        },
                        '& .MuiDataGrid-cell:last-child': {
                          borderRight: 'none', // Remove border from last column
                        },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                        '& .MuiDataGrid-row.Mui-selected': {
                          backgroundColor: '#1a1a1a',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                        },
                        '& .MuiDataGrid-columnSeparator': { 
                          visibility: 'visible',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': { color: '#ffffff !important' },
                        '& .MuiDataGrid-iconButtonContainer': { color: '#ffffff !important' },
                        '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                        '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          color: '#ffffff !important',
                        },
                        '& .MuiSelect-icon': { color: '#ffffff !important' },
                        '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                        '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                        '& .MuiDataGrid-root': {
                          overflowY: 'auto !important',
                          margin: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          overflowY: 'auto !important',
                        },
                        '& .MuiDataGrid-main': {
                          padding: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          backgroundColor: '#1e1e1e',
                          color: '#ffffff !important',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 1,
                          boxSizing: 'border-box',
                        },
                      }}
                      rowBuffer={10}
                      rowThreshold={5}
                    />
                  </Box>
                </Box>
              </Box>
              {/* Bet Slip Panel */}
              <Box sx={{ width: '25%', height: 'calc(100vh - 128px)', boxSizing: 'border-box' }}>
                <Box sx={{ backgroundColor: '#1a1a1a', borderRadius: '12px', height: '100%', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                      Bet Slip ({betSlipBets.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      sx={{
                        color: '#ffffff',
                        borderColor: '#ffffff',
                        fontSize: '0.8rem',
                        padding: '4px 8px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: '#ffffff',
                        },
                      }}
                      onClick={() => setBetSlip([])}
                      disabled={betSlipBets.length === 0}
                    >
                      Clear Bet Slip
                    </Button>
                  </Stack>
                  <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
                    {betSlipBets.length === 0 ? (
                      <Typography sx={{ color: '#bbbbbb', textAlign: 'center', fontStyle: 'italic' }}>
                        No bets added to the slip.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {betSlipBets.map(bet => (
                          <Box
                            key={bet.id}
                            sx={{
                              p: 1.5,
                              backgroundColor: '#222222',
                              borderRadius: '8px',
                              border: '1px solid #333',
                              transition: 'background-color 0.2s',
                              '&:hover': {
                                backgroundColor: '#2a2a2a',
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <IconButton
                                size="small"
                                onClick={() => setBetSlip(betSlip.filter(id => id !== bet.id))}
                                sx={{ color: '#ffffff', '&:hover': { color: '#ff4444' } }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500 }}>
                                  {bet.playerName} | {bet.betTypeName} | {bet.sidepoints}
                                </Typography>
                                <Typography sx={{ color: '#cccccc', fontSize: '0.85rem' }}>
                                  {bet.leagueName} | {bet.eventShortName}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ color: '#cccccc', fontSize: '0.85rem', textAlign: 'right' }}>
                                  Model%: <span style={{ color: '#ffffff' }}>{(bet.trueWin * 100).toFixed(1)}%</span>
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                  {/* Fixed Odds/Payout/EV Section at the Bottom */}
                  <Box
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      backgroundColor: '#222222',
                      borderTop: '2px solid #444',
                      p: 2,
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500, mb: 1 }}>
                      Odds: <span style={{ color: '#ffffff' }}>{slipWinInfo.odds}</span>
                    </Typography>
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500, mb: 1 }}>
                      Payout: <span style={{ color: '#ffffff' }}>{slipWinInfo.payout}</span>
                    </Typography>
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500 }}>
                      EV: <span style={{ color: slipWinInfo.ev === 0 ? '#bbbbbb' : slipWinInfo.ev > 0 ? '#00ff00' : '#ff0000' }}>
                        {slipWinInfo.ev.toFixed(1)}%
                      </span>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default PickemDataGrid;